/**
 * src/app/marketplace/hooks/marketplaceSessionStore.js
 * ---------------------------------------------------------------------------
 * Single source of truth for the marketplace browser session.
 * 
 * The session lives in an httpOnly cookie on the API origin and is
 * unreadable here by design. The localStorage hint is cosmetic — it makes the
 * first render correct; the server is always the authority.
 * ---------------------------------------------------------------------------
 */

import axios from 'axios';
import config from '../../../config';

const HINT_KEY = 'marketplaceSession';
const withSession = { withCredentials: true };

const SIGNED_OUT = {
	confirmed: false,
	providerKey: null,
	signedIn: false,
	connected: false,
	status: 'none',
	hasOpportunity: false,
	email: '',
	companyName: ''
};

const listeners = new Set();

let state = seedFromHint();
let inFlight = null;

// Monotonic request id. A /session response may only commit if no newer request has started since.
let requestSeq = 0;
let confirmed = false;


/** Current snapshot. */
export function getSnapshot() {
	return state;
}


/**
 * Subscribe to snapshot changes.
 *
 * @param {function} listener
 * @returns {function} unsubscribe func
 */
export function subscribe(listener) {
	listeners.add(listener);

	return function unsubscribe() {
		listeners.delete(listener);
	};
}


/**
 * Reconcile with the server. Concurrent callers share one request, so several
 * consumers mounting together still produce a single GET /session.
 *
 * @param {object}  [options]
 * @param {boolean} [options.force]  start a fresh request even if one is in flight.
 * @returns {Promise<object>} the resulting snapshot
 */
export function refresh(options) {
	const providerKey = state.providerKey;

	if (!providerKey) return Promise.resolve(state);
	if (inFlight && !(options && options.force)) return inFlight;

	const seq = ++requestSeq;

	const request = axios.get(`${config.partnerApiUrl}/partners/${providerKey}/session`, withSession)
		.then(({ data }) => fromSessionResponse(providerKey, data))
		.catch(() => {
			// An unreachable or failed check then treat it as signed out rather than offering controls that would fail when clicked.
			return SIGNED_OUT;
		})
		.then((next) => {
			if (seq !== requestSeq) return state;

			if (inFlight === request) inFlight = null;

			confirmed = true;

			return commit(next);
		});

	inFlight = request;

	return request;
}


/**
 * Ask the server about a provider the browser holds no stored hint for.
 *
 * @param {string} providerKey
 * @returns {Promise<object>} the resulting snapshot
 */
export function probe(providerKey) {
	if (!providerKey) return Promise.resolve(state);

	// Already tracking this provider — the stored hint stands, just confirm it.
	if (state.providerKey === providerKey) return refresh();

	commit(Object.assign({}, SIGNED_OUT, { providerKey: providerKey }));

	return refresh({ force: true });
}


/** End the browser session. */
export function signOut() {
	const providerKey = state.providerKey;

	return axios.post(`${config.partnerApiUrl}/partners/${providerKey}/signout`, {}, withSession)
		.catch(() => null)
		.then(() => commit(SIGNED_OUT));
}


/**
 * Revoke the marketplace grant. Rejects on failure so the caller can surface it.
 *
 * @param {string} disconnectPath
 * @returns {Promise<boolean>}
 */
export function disconnect(disconnectPath) {
	return axios.post(`${config.partnerApiUrl}${disconnectPath}`, {}, withSession)
		.then(() => {
			commit(Object.assign({}, state, { connected: false, status: 'revoked' }));

			// force session refresh
			refresh({ force: true });

			return true;
		});
}


// Registers a listener on session hint in local storage.
if (typeof window !== 'undefined' && window.addEventListener) {
	window.addEventListener('storage', function (event) {
		if (event.key === HINT_KEY) commit(seedFromHint());
	});
}


// ===========================================================================
// Helpers
// ===========================================================================

/**
 * Map a GET /session response onto a snapshot.
 *
 * @param {string} providerKey  the provider the response describes
 * @param {object} data         the parsed response body
 * @returns {object} snapshot
 */
function fromSessionResponse(providerKey, data) {
	if (!data || !data.signedIn) return SIGNED_OUT;

	return {
		providerKey: providerKey,
		signedIn: true,
		connected: !!data.connected,
		status: data.status || 'unknown',
		hasOpportunity: !!data.hasOpportunity,
		email: data.email || '',
		companyName: data.companyName || ''
	};
}


// Build a snapshot from the localStorage hint.
function seedFromHint() {
	let hint = null;

	try {
		const raw = window.localStorage.getItem(HINT_KEY);

		hint = raw ? JSON.parse(raw) : null;
	} catch (e) {
		// Storage unavailable
	}

	if (!hint || !hint.provider || !hint.signedIn) return SIGNED_OUT;

	return {
		confirmed: false,
		providerKey: hint.provider,
		signedIn: true,
		connected: !!hint.connected,
		status: hint.status || 'unknown',
		hasOpportunity: !!hint.hasOpportunity,
		email: hint.email || '',
		companyName: hint.companyName || ''
	};
}


/** Replace the snapshot, persist the hint, and notify subscribers. */
function commit(next) {
	state = Object.assign({}, next, { confirmed: confirmed });

	try {
		if (next.signedIn) {
			window.localStorage.setItem(HINT_KEY, JSON.stringify({
				provider: next.providerKey,
				signedIn: true,
				connected: next.connected,
				status: next.status,
				hasOpportunity: next.hasOpportunity,
				email: next.email,
				companyName: next.companyName
			}));
		} else {
			window.localStorage.removeItem(HINT_KEY);
		}
	} catch (e) {
		// Storage unavailable
	}

	listeners.forEach(function (listener) {
		listener();
	});

	return state;
}
